import { AudioPlaceholder } from '@hendrx/icons/AudioPlaceholder';
import { AudioToolbar } from '@hendrx/icons/AudioToolbar';
import { Box, Button, Flex, Popover, TextInput } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { RichTextEditor } from '@mantine/tiptap';
import { Editor } from '@tiptap/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useUiLanguage } from 'shared/utils/useUiLanguage';

import classes from './InsertAudio.module.scss';
import { Manifest as AudioManifest } from '../../../../contentObjects/widgets/Audio/meta/Manifest';
import { FormControl } from '../../../controlledPanels/FormControl';
import { UploadFile } from '../../../upload/UploadFile/UploadFile';

export type InsertAudioProps = {
  editor: Editor | null;
};

type InsertAudioFormValues = {
  alt: string;
  src: string;
};

const initialValues: InsertAudioFormValues = { alt: '', src: '' };

export function InsertAudio(props: InsertAudioProps) {
  const { editor } = props;

  const { uiDirection } = useUiLanguage();
  const { t } = useTranslation('widgets.insertAudio');

  const [opened, { toggle, close }] = useDisclosure(false);

  const schema = useMemo(
    () =>
      z.object({
        alt: z
          .string()
          .trim()
          .min(1, { message: t('addAlternativeTextError') ?? '' }),
        // Not `.url()`: file storage may return a relative path, which would silently block Confirm.
        src: z.string().min(1, { message: t('uploadAudioMissingError') ?? '' })
      }),
    [t]
  );

  const form = useForm<InsertAudioFormValues>({
    initialValues,
    validateInputOnChange: false,
    validate: zodResolver(schema)
  });

  const handleResetForm = () => {
    form.reset();
    close();
  };

  const handleInsertAudio = (values: InsertAudioFormValues) => {
    // No `.focus()` first: the popover leaves the stored selection intact, focusing would move the caret.
    editor?.commands.setAudio({ alt: values.alt.trim(), src: values.src });
    handleResetForm();
  };

  return (
    <Popover
      opened={opened}
      position="bottom-end"
      withArrow
      shadow="md"
      trapFocus
      onClose={handleResetForm}
      // The fixed toolbar closes on any click outside itself, so a portalled dropdown would dismiss it.
      withinPortal={false}
    >
      <Popover.Target>
        <RichTextEditor.Control
          onClick={toggle}
          title={t('title')}
          aria-label={t('title')}
          active={opened}
          data-testid="texteditor-insert-audio"
        >
          <AudioToolbar />
        </RichTextEditor.Control>
      </Popover.Target>
      <Popover.Dropdown>
        <Box dir={uiDirection} component="form" onReset={handleResetForm} onSubmit={form.onSubmit(handleInsertAudio)}>
          <Flex className={classes.formRow}>
            <FormControl title={t('uploadAudio')} className={classes.uploadContainer}>
              <UploadFile
                fileType="audio"
                previewMaxHeight={130}
                src={form.values.src}
                alt={t('uploadAudio') ?? ''}
                // Not AudioUpload: its fills are hard-coded, so it cannot turn red on rejection.
                Icon={AudioPlaceholder}
                text={t('uploadAudio') ?? ''}
                maxSize={AudioManifest.additionalData?.maxFileSizeBytes}
                maxFiles={AudioManifest.additionalData?.maxFiles}
                accept={AudioManifest.additionalData?.acceptFileType ?? []}
                onDrop={src => form.setFieldValue('src', src)}
                onDelete={() => form.setFieldValue('src', '')}
                dropError={t('unableUpload')}
                errorMessage={form.errors.src ? String(form.errors.src) : undefined}
                customClassNames={{ dropzoneRoot: classes.dropzone }}
                dataTestId="texteditor-audio"
                // FilePreview renders nothing for non-image types.
                customPreview={
                  <Flex
                    justify="center"
                    align="center"
                    className={classes.audioPreview}
                    data-testid="texteditor-audio-preview"
                  >
                    <AudioPlaceholder />
                  </Flex>
                }
              />
            </FormControl>
            <Box className={classes.altColumn}>
              <FormControl title={t('addAlternativeText')} withAsterisk>
                <TextInput
                  {...form.getInputProps('alt')}
                  className={classes.altInput}
                  placeholder={t('addAlternativeTextPlaceholder') ?? ''}
                  data-testid="texteditor-audio-alternative-text-field"
                />
              </FormControl>
              <Flex justify="flex-end" className={classes.footerButtons}>
                <Button type="reset" variant="outline" data-testid="texteditor-audio-cancel">
                  {t('cancel')}
                </Button>
                <Button type="submit" className={classes.submitButton} data-testid="texteditor-audio-confirm">
                  {t('ok')}
                </Button>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
}
